/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const ALL_VENUES        = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];
const ORDER_FROM_VENUES = ['Prep Kitchen', 'Box Jordaan', 'Box Plantage', 'Sassys'];
const DEPTS             = ['kitchen', 'bar'];
const FULFILMENT_TYPES  = ['external', 'internal', 'both'];
const DAYS              = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NUMS          = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };

const C = {
  yellow:      '#F7EC71',
  yellowLight: '#FEFCE8',
  yellowDark:  '#E4AC49',
  orange:      '#E4AC49',
  orangeLight: '#FDF3DC',
  green:       '#7EA594',
  greenLight:  '#EAF2EF',
  cream:       '#F7F6EF',
  charcoal:    '#2F3A42',
  navy:        '#445366',
  mid:         '#7A8A96',
  border:      '#D8DEE3',
  skyBlue:     '#BCD9E5',
  skyBlueLight:'#EAF4F8',
  skyText:     '#3A8FA3',
  white:       '#FFFFFF',
  red:         '#DC3545',
  redLight:    '#FFF0F0',
  amber:       '#E07B00',
  amberLight:  '#FFF3E0',
  purple:      '#7C3AED',
  purpleLight: '#F3EEFF',
};

const FULFILMENT_LABELS = { external: 'External', internal: 'Internal', both: 'Both' };
const FULFILMENT_COLORS = {
  external: { bg: C.skyBlueLight, color: C.skyText },
  internal: { bg: C.greenLight,   color: C.green   },
  both:     { bg: C.amberLight,   color: C.amber   },
};
const DEPT_COLORS = {
  kitchen: { bg: C.orangeLight, color: C.orange },
  bar:     { bg: C.skyBlueLight, color: C.skyText },
};

const empty = {
  name: '', unit: '', price: '',
  dept: '',  // required — no default
  category: '', supplierId: '',
  fulfilmentType: 'external',
  availableAt: [], orderFromVenues: [],
  defaultFulfiller: 'Prep Kitchen',
  stockTracked: false,
  stocktakeDays: [],
  active: true,
};

// ── Shared style helpers ──────────────────────────────────────────────
const pill = (active, activeColor, activeBg) => ({
  padding: '0.35rem 0.9rem',
  borderRadius: '999px',
  border: '1.5px solid',
  borderColor: active ? (activeColor || C.navy) : C.border,
  background:  active ? (activeBg || activeColor || C.navy) : C.white,
  color:       active ? C.white : C.mid,
  fontSize: '0.85rem',
  cursor: 'pointer',
  fontWeight: active ? 700 : 500,
  transition: 'all 0.15s',
});

const smallPill = (active, color) => ({
  padding: '0.28rem 0.7rem',
  borderRadius: '999px',
  border: `1.5px solid ${active ? (color || C.navy) : C.border}`,
  background:  active ? (color || C.navy) : C.white,
  color:       active ? C.white : C.mid,
  fontSize: '0.8rem',
  cursor: 'pointer',
  fontWeight: active ? 700 : 500,
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
});

const inputStyle = {
  width: '100%', padding: '0.5rem 0.65rem',
  border: `1.5px solid ${C.border}`, borderRadius: '8px',
  fontSize: '0.9rem', boxSizing: 'border-box',
  color: C.charcoal, outline: 'none',
  transition: 'border-color 0.15s',
};

const selectStyle = {
  ...inputStyle,
  background: C.white,
  cursor: 'pointer',
};

function FieldLabel({ text, sub, required, error }) {
  return (
    <label style={{ fontSize: '0.75rem', color: error ? C.red : C.mid, display: 'block', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {text}
      {required && <span style={{ color: C.red, marginLeft: '0.2rem' }}>*</span>}
      {sub && <span style={{ color: C.border, fontWeight: 400, textTransform: 'none', marginLeft: '0.3rem' }}>{sub}</span>}
      {error && <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 700 }}>{error}</span>}
    </label>
  );
}

// ── Par Level Editor ──────────────────────────────────────────────────
function ParLevelEditor({ product, onClose }) {
  const [rows,   setRows]   = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    supabase.from('par_levels').select('*').eq('product_id', product.id)
      .then(({ data }) => { if (data) setRows(data); });
  }, [product.id]);

  const venues    = product.availableAt || product.available_at || [];
  const days      = product.stocktakeDays || product.stocktake_days || [];
  const dayLabels = days.length > 0 ? days : DAYS;

  function getVal(venue, day) {
    const row = rows.find(r => r.venue === venue && r.day_of_week === DAY_NUMS[day]);
    return row?.par_level ?? '';
  }

  function setVal(venue, day, val) {
    const dayNum = DAY_NUMS[day];
    setRows(prev => {
      const exists = prev.find(r => r.venue === venue && r.day_of_week === dayNum);
      if (exists) return prev.map(r => r.venue === venue && r.day_of_week === dayNum ? { ...r, par_level: val } : r);
      return [...prev, { product_id: product.id, venue, day_of_week: dayNum, par_level: val, unit: product.unit }];
    });
  }

  async function handleSave() {
    setSaving(true);
    for (const row of rows) {
      const val = parseFloat(row.par_level);
      if (isNaN(val)) continue;
      await supabase.from('par_levels').upsert({
        product_id:  product.id,
        venue:       row.venue,
        day_of_week: row.day_of_week,
        par_level:   val,
        unit:        product.unit,
      }, { onConflict: 'product_id,venue,day_of_week' });
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (venues.length === 0) {
    return (
      <div style={{ padding: '1rem', color: C.mid, fontSize: '0.88rem' }}>
        No venues assigned. Edit the product to add venues first.
      </div>
    );
  }

  return (
    <div style={{ padding: '1.25rem', background: C.yellowLight, borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: C.navy }}>Par Levels — {product.name}</span>
          <span style={{ fontSize: '0.78rem', color: C.mid, marginLeft: '0.5rem' }}>per venue · per day</span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.mid, cursor: 'pointer', fontSize: '0.85rem', padding: '0.25rem 0.5rem' }}>✕ Close</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: C.mid, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', minWidth: '120px' }}>Venue</th>
              {dayLabels.map(d => (
                <th key={d} style={{ padding: '0.4rem 0.6rem', textAlign: 'center', color: C.mid, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', minWidth: '62px' }}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {venues.map(venue => (
              <tr key={venue}>
                <td style={{ padding: '0.4rem 0.75rem', fontWeight: 600, color: C.charcoal, whiteSpace: 'nowrap' }}>{venue}</td>
                {dayLabels.map(day => (
                  <td key={day} style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>
                    <input type='number' min='0' step='0.5'
                      value={getVal(venue, day)}
                      onChange={e => setVal(venue, day, e.target.value)}
                      placeholder='—'
                      style={{ width: '56px', padding: '0.28rem 0.3rem', border: `1.5px solid ${C.border}`, borderRadius: '5px', textAlign: 'center', fontSize: '0.88rem', outline: 'none', color: C.charcoal }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '0.4rem 1.2rem', background: saving ? C.border : C.navy, color: C.white, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
          {saving ? 'Saving…' : 'Save Par Levels'}
        </button>
        {saved && <span style={{ color: C.green, fontSize: '0.85rem', fontWeight: 700 }}>✓ Saved</span>}
        <span style={{ fontSize: '0.78rem', color: C.border }}>Leave blank = no par set for that day</span>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────
export default function Products({ products, setProducts }) {
  const [suppliers, setSuppliers]       = useState([]);
  const [categories, setCategories]     = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [showAdd, setShowAdd]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState(null);
  const [formData, setFormData]         = useState(empty);
  const [formErrors, setFormErrors]     = useState({});
  const [filterDept, setFilterDept]     = useState('all');
  const [filterFulfilment, setFilterFulfilment] = useState('all');
  const [filterVenue, setFilterVenue]   = useState('all');
  const [expandedPar, setExpandedPar]   = useState(null);
  const [searchQuery, setSearchQuery]   = useState('');

  useEffect(() => {
    supabase.from('suppliers').select('*').eq('active', true).order('name')
      .then(({ data }) => { if (data) setSuppliers(data); });
    supabase.from('categories').select('*').eq('active', true).order('name')
      .then(({ data }) => { if (data) setCategories(data); });
  }, []);

  const displayed = products
    .filter(p => showInactive ? true : p.active)
    .filter(p => filterDept       === 'all' || p.dept === filterDept)
    .filter(p => filterFulfilment === 'all' || (p.fulfilmentType || p.fulfilment_type) === filterFulfilment)
    .filter(p => filterVenue      === 'all' || (p.availableAt || p.available_at || []).includes(filterVenue))
    .filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  function validateForm() {
    const errors = {};
    if (!formData.name.trim())  errors.name = 'Required';
    if (!formData.unit.trim())  errors.unit = 'Required';
    if (!formData.dept)         errors.dept = 'Required';
    return errors;
  }

  function handleEdit(product) {
    setFormData({
      name:             product.name              || '',
      unit:             product.unit              || '',
      price:            product.price?.toString() || '',
      dept:             product.dept              || '',
      category:         product.category          || '',
      supplierId:       product.supplierId        || product.supplier_id || '',
      fulfilmentType:   product.fulfilmentType    || product.fulfilment_type || 'external',
      availableAt:      product.availableAt       || product.available_at     || [],
      orderFromVenues:  product.orderFromVenues   || product.order_from_venues || [],
      defaultFulfiller: product.defaultFulfiller  || product.default_fulfiller || 'Prep Kitchen',
      stockTracked:     product.stockTracked      || product.stock_tracked    || false,
      stocktakeDays:    product.stocktakeDays     || product.stocktake_days   || [],
      active:           product.active ?? true,
    });
    setEditingId(product.id);
    setShowAdd(true);
    setError(null);
    setFormErrors({});
    setExpandedPar(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleToggleVenue(venue) {
    setFormData(prev => ({
      ...prev,
      availableAt: prev.availableAt.includes(venue)
        ? prev.availableAt.filter(v => v !== venue)
        : [...prev.availableAt, venue],
    }));
  }

  function handleToggleOrderFrom(venue) {
    setFormData(prev => ({
      ...prev,
      orderFromVenues: prev.orderFromVenues.includes(venue)
        ? prev.orderFromVenues.filter(v => v !== venue)
        : [...prev.orderFromVenues, venue],
    }));
  }

  function handleToggleStocktakeDay(day) {
    setFormData(prev => ({
      ...prev,
      stocktakeDays: prev.stocktakeDays.includes(day)
        ? prev.stocktakeDays.filter(d => d !== day)
        : [...prev.stocktakeDays, day],
    }));
  }

  async function handleSave() {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setSaving(true);
    setError(null);

    const payload = {
      name:              formData.name,
      unit:              formData.unit,
      price:             parseFloat(formData.price) || 0,
      dept:              formData.dept,
      category:          formData.category,
      supplier_id:       formData.supplierId || null,
      fulfilment_type:   formData.fulfilmentType,
      available_at:      formData.availableAt,
      order_from_venues: formData.orderFromVenues,
      default_fulfiller: formData.defaultFulfiller || 'Prep Kitchen',
      stock_tracked:     formData.stockTracked,
      stocktake_days:    formData.stockTracked ? formData.stocktakeDays : [],
      active:            formData.active,
    };

    const normalize = (data) => ({
      ...data,
      fulfilmentType:   data.fulfilment_type    || 'external',
      availableAt:      data.available_at       || [],
      orderFromVenues:  data.order_from_venues  || [],
      defaultFulfiller: data.default_fulfiller  || 'Prep Kitchen',
      stockTracked:     data.stock_tracked      || false,
      stocktakeDays:    data.stocktake_days     || [],
      supplierId:       data.supplier_id,
      supplierName:     suppliers.find(s => s.id === data.supplier_id)?.name || '',
    });

    if (editingId) {
      const { error: err } = await supabase.from('products').update(payload).eq('id', editingId);
      if (err) { setError('Save failed: ' + err.message); setSaving(false); return; }
      setProducts(prev => prev.map(p => p.id === editingId ? normalize({ ...p, ...payload, id: editingId }) : p));
      setEditingId(null);
    } else {
      const { data, error: err } = await supabase.from('products').insert(payload).select().single();
      if (err) { setError('Save failed: ' + err.message); setSaving(false); return; }
      setProducts(prev => [...prev, normalize(data)]);
    }

    setFormData(empty);
    setFormErrors({});
    setShowAdd(false);
    setSaving(false);
  }

  function handleCancel() {
    setFormData(empty);
    setEditingId(null);
    setShowAdd(false);
    setError(null);
    setFormErrors({});
  }

  async function handleToggleActive(id) {
    const product = products.find(p => p.id === id);
    if (!product) return;
    const newActive = !product.active;
    const { error: err } = await supabase.from('products').update({ active: newActive }).eq('id', id);
    if (err) { setError('Update failed: ' + err.message); return; }
    setProducts(prev => prev.map(p => p.id === id ? { ...p, active: newActive } : p));
  }

  const formValid = formData.name.trim() && formData.unit.trim() && formData.dept;

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.5rem', fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>Products</h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: C.mid }}>
            Manage catalogue, fulfilment, stocktake days and par levels
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: C.mid, cursor: 'pointer' }}>
            <input type='checkbox' checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
              style={{ accentColor: C.navy }} />
            Show inactive
          </label>
          <button onClick={() => showAdd ? handleCancel() : setShowAdd(true)}
            style={{
              padding: '0.5rem 1.25rem',
              background: showAdd ? C.white : C.navy,
              color: showAdd ? C.navy : C.white,
              border: `1.5px solid ${C.navy}`,
              borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem',
              transition: 'all 0.15s',
            }}>
            {showAdd ? 'Cancel' : '+ Add Product'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: C.redLight, border: `1px solid ${C.red}40`, borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', color: C.red, fontSize: '0.88rem', fontWeight: 500 }}>
          {error}
        </div>
      )}

      {/* ADD / EDIT FORM */}
      {showAdd && (
        <div style={{ background: C.white, borderRadius: '12px', boxShadow: '0 4px 20px rgba(68,83,102,0.1)', padding: '1.75rem', marginBottom: '2rem', border: `1.5px solid ${editingId ? C.yellowDark : C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: C.navy }}>
              {editingId ? '✏️ Edit Product' : '✨ New Product'}
            </h3>
            {editingId && (
              <span style={{ background: C.yellowLight, color: C.yellowDark, padding: '0.15rem 0.65rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 }}>
                Editing
              </span>
            )}
          </div>

          {/* Row 1: Basic fields */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
            <div>
              <FieldLabel text='Name' required error={formErrors.name} />
              <input type='text' value={formData.name}
                onChange={e => { setFormData(p => ({ ...p, name: e.target.value })); setFormErrors(p => ({ ...p, name: null })); }}
                placeholder='e.g. Chicken Thighs'
                style={{ ...inputStyle, borderColor: formErrors.name ? C.red : formData.name ? C.navy : C.border }}
              />
            </div>
            <div>
              <FieldLabel text='Unit' required error={formErrors.unit} />
              <input type='text' value={formData.unit}
                onChange={e => { setFormData(p => ({ ...p, unit: e.target.value })); setFormErrors(p => ({ ...p, unit: null })); }}
                placeholder='e.g. kg, piece'
                style={{ ...inputStyle, borderColor: formErrors.unit ? C.red : formData.unit ? C.navy : C.border }}
              />
            </div>
            <div>
              <FieldLabel text='Transfer Price (€)' />
              <input type='number' value={formData.price}
                onChange={e => setFormData(p => ({ ...p, price: e.target.value }))}
                placeholder='0.00'
                style={inputStyle}
              />
            </div>

            <div>
              <FieldLabel text='Dept' required error={formErrors.dept} />
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {DEPTS.map(d => {
                  const dc = DEPT_COLORS[d];
                  const active = formData.dept === d;
                  return (
                    <button key={d} onClick={() => { setFormData(p => ({ ...p, dept: d })); setFormErrors(p => ({ ...p, dept: null })); }}
                      style={{
                        flex: 1, padding: '0.5rem 0',
                        borderRadius: '8px', border: `1.5px solid ${active ? dc.color : C.border}`,
                        background: active ? dc.bg : C.white, color: active ? dc.color : C.mid,
                        cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: '0.85rem',
                        transition: 'all 0.15s',
                      }}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </button>
                  );
                })}
              </div>
              {formErrors.dept && <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: C.red }}>Please select a department</p>}
            </div>

            <div>
              <FieldLabel text='Fulfilment Type' />
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                {FULFILMENT_TYPES.map(t => {
                  const fc = FULFILMENT_COLORS[t];
                  const active = formData.fulfilmentType === t;
                  return (
                    <button key={t} onClick={() => setFormData(p => ({ ...p, fulfilmentType: t }))}
                      style={{
                        flex: 1, padding: '0.5rem 0',
                        borderRadius: '8px', border: `1.5px solid ${active ? fc.color : C.border}`,
                        background: active ? fc.bg : C.white, color: active ? fc.color : C.mid,
                        cursor: 'pointer', fontWeight: active ? 700 : 500, fontSize: '0.78rem',
                        transition: 'all 0.15s',
                      }}>
                      {FULFILMENT_LABELS[t]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <FieldLabel text='Category' sub='(optional)' />
              <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))} style={selectStyle}>
                <option value=''>— Select —</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            {(formData.fulfilmentType === 'external' || formData.fulfilmentType === 'both') && (
              <div>
                <FieldLabel text='Supplier' />
                <select value={formData.supplierId} onChange={e => setFormData(p => ({ ...p, supplierId: e.target.value }))} style={selectStyle}>
                  <option value=''>— Select —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Available At */}
          <div style={{ marginBottom: '1.25rem' }}>
            <FieldLabel text='Available At' sub='— venues that stock this product' />
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {ALL_VENUES.map(v => (
                <button key={v} onClick={() => handleToggleVenue(v)}
                  style={pill(formData.availableAt.includes(v))}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Internal Fulfilment */}
          {(formData.fulfilmentType === 'internal' || formData.fulfilmentType === 'both') && (
            <div style={{ marginBottom: '1.25rem' }}>
              <FieldLabel text='Can Be Ordered From' sub='— venues that can supply this product' />
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {ORDER_FROM_VENUES.map(v => (
                  <button key={v} onClick={() => handleToggleOrderFrom(v)}
                    style={pill(formData.orderFromVenues.includes(v), C.green)}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Default Fulfiller */}
          {(formData.fulfilmentType === 'internal' || formData.fulfilmentType === 'both') && formData.orderFromVenues.length > 0 && (
            <div style={{ marginBottom: '1.25rem', maxWidth: '260px' }}>
              <FieldLabel text='Default Fulfiller' sub='— determines stocktake tab + fulfilment routing' />
              <select value={formData.defaultFulfiller} onChange={e => setFormData(p => ({ ...p, defaultFulfiller: e.target.value }))} style={selectStyle}>
                {formData.orderFromVenues.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          )}

          {/* Stock Tracked */}
          <div style={{ marginBottom: '1.25rem', padding: '1.1rem', background: formData.stockTracked ? C.yellowLight : C.cream, borderRadius: '10px', border: `1.5px solid ${formData.stockTracked ? C.yellowDark : C.border}`, transition: 'all 0.2s' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.9rem', cursor: 'pointer', marginBottom: formData.stockTracked ? '1rem' : 0 }}>
              <input type='checkbox' checked={formData.stockTracked}
                onChange={e => setFormData(p => ({ ...p, stockTracked: e.target.checked, stocktakeDays: e.target.checked ? p.stocktakeDays : [] }))}
                style={{ width: '16px', height: '16px', accentColor: C.navy, cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600, color: C.charcoal }}>Track stock</span>
              <span style={{ fontSize: '0.8rem', color: C.mid }}>(appears in stocktake)</span>
            </label>

            {formData.stockTracked && (
              <>
                <FieldLabel text='Stocktake Days' sub='— days this product appears in stocktake' />
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {DAYS.map(d => (
                    <button key={d} onClick={() => handleToggleStocktakeDay(d)}
                      style={pill(formData.stocktakeDays.includes(d), C.navy)}>
                      {d}
                    </button>
                  ))}
                </div>
                {formData.stocktakeDays.length === 0 && (
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: C.amber }}>⚠ Select at least one stocktake day</p>
                )}
              </>
            )}
          </div>

          {/* Active toggle in form */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.9rem', cursor: 'pointer' }}>
              <input type='checkbox' checked={formData.active}
                onChange={e => setFormData(p => ({ ...p, active: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: C.green, cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600, color: formData.active ? C.green : C.mid }}>
                {formData.active ? 'Active' : 'Inactive'}
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={handleSave} disabled={!formValid || saving}
              style={{
                padding: '0.6rem 1.75rem',
                background: !formValid || saving ? C.border : C.navy,
                color: !formValid || saving ? C.mid : C.white,
                border: 'none', borderRadius: '8px', cursor: !formValid || saving ? 'default' : 'pointer',
                fontWeight: 800, fontSize: '0.92rem', transition: 'all 0.15s',
              }}>
              {saving ? 'Saving…' : editingId ? 'Update Product' : 'Save Product'}
            </button>
            <button onClick={handleCancel}
              style={{ padding: '0.6rem 1.25rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', color: C.mid }}>
              Cancel
            </button>
            {!formData.dept && (
              <span style={{ fontSize: '0.8rem', color: C.amber }}>⚠ Department is required</span>
            )}
          </div>
        </div>
      )}

      {/* FILTERS */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', background: C.white, borderRadius: '10px', padding: '0.85rem 1rem', border: `1px solid ${C.border}` }}>
        {/* Search */}
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder='Search products…'
          style={{ ...inputStyle, width: '180px', padding: '0.4rem 0.75rem', flexShrink: 0, borderColor: searchQuery ? C.navy : C.border }}
        />

        {/* Dept pills */}
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: C.mid, fontWeight: 600, textTransform: 'uppercase' }}>Dept:</span>
          {['all', ...DEPTS].map(d => {
            const dc = DEPT_COLORS[d];
            return (
              <button key={d} onClick={() => setFilterDept(d)}
                style={smallPill(filterDept === d, d === 'all' ? C.navy : dc?.color)}>
                {d === 'all' ? 'All' : d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            );
          })}
        </div>

        {/* Fulfilment pills */}
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: C.mid, fontWeight: 600, textTransform: 'uppercase' }}>Type:</span>
          {['all', ...FULFILMENT_TYPES].map(f => {
            const fc = FULFILMENT_COLORS[f];
            return (
              <button key={f} onClick={() => setFilterFulfilment(f)}
                style={smallPill(filterFulfilment === f, f === 'all' ? C.navy : fc?.color)}>
                {f === 'all' ? 'All' : FULFILMENT_LABELS[f]}
              </button>
            );
          })}
        </div>

        {/* Venue dropdown */}
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: C.mid, fontWeight: 600, textTransform: 'uppercase' }}>Venue:</span>
          <select value={filterVenue} onChange={e => setFilterVenue(e.target.value)}
            style={{ ...selectStyle, width: 'auto', padding: '0.35rem 0.65rem', fontSize: '0.82rem' }}>
            <option value='all'>All</option>
            {ALL_VENUES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <span style={{ fontSize: '0.78rem', color: C.mid, marginLeft: 'auto', fontWeight: 600 }}>
          {displayed.length} product{displayed.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* PRODUCT TABLE */}
      <div style={{ background: C.white, borderRadius: '12px', boxShadow: '0 2px 12px rgba(68,83,102,0.08)', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${C.border}`, background: C.cream }}>
              {['Name', 'Dept', 'Fulfilment', 'Fulfiller', 'Category', 'Supplier', 'Available At', 'Stocktake', 'Tracked', 'Status', ''].map(h => (
                <th key={h} style={{ padding: '0.7rem 1rem', textAlign: 'left', fontSize: '0.68rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && (
              <tr><td colSpan={11} style={{ padding: '2.5rem', color: C.mid, textAlign: 'center', fontSize: '0.88rem' }}>No products found.</td></tr>
            )}
            {displayed.map(p => {
              const ft              = p.fulfilmentType || p.fulfilment_type || 'external';
              const ftStyle         = FULFILMENT_COLORS[ft] || FULFILMENT_COLORS.external;
              const deptStyle       = DEPT_COLORS[p.dept] || { bg: C.cream, color: C.mid };
              const supplierName    = p.supplierName || suppliers.find(s => s.id === (p.supplierId || p.supplier_id))?.name || '—';
              const availableAt     = p.availableAt || p.available_at || [];
              const stDays          = p.stocktakeDays || p.stocktake_days || [];
              const tracked         = p.stockTracked || p.stock_tracked || false;
              const defaultFulfiller = p.defaultFulfiller || p.default_fulfiller || '—';
              const isParOpen       = expandedPar === p.id;

              return (
                <>
                  <tr key={p.id} style={{
                    borderBottom: isParOpen ? 'none' : `1px solid ${C.border}`,
                    opacity: p.active ? 1 : 0.45,
                    background: isParOpen ? C.yellowLight : 'transparent',
                    transition: 'background 0.15s',
                  }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 600, fontSize: '0.9rem', color: C.charcoal }}>
                      {p.name}
                      <div style={{ fontSize: '0.72rem', color: C.mid, fontWeight: 400, marginTop: '0.1rem' }}>{p.unit}</div>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {p.dept ? (
                        <span style={{ background: deptStyle.bg, color: deptStyle.color, padding: '0.18rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                          {p.dept.charAt(0).toUpperCase() + p.dept.slice(1)}
                        </span>
                      ) : (
                        <span style={{ background: C.redLight, color: C.red, padding: '0.18rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                          ⚠ No dept
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ background: ftStyle.bg, color: ftStyle.color, padding: '0.18rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                        {FULFILMENT_LABELS[ft]}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.82rem', color: C.charcoal, fontWeight: 500 }}>
                      {ft === 'internal' || ft === 'both' ? defaultFulfiller : '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: C.mid, fontSize: '0.82rem' }}>{p.category || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.82rem', color: C.charcoal }}>{supplierName}</td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: C.mid }}>
                      {availableAt.map(v => v.replace('Box ', '')).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: tracked ? C.charcoal : C.border }}>
                      {tracked ? (stDays.length > 0 ? stDays.join(', ') : 'All days') : '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {tracked
                        ? <span style={{ color: C.green, fontWeight: 700, fontSize: '0.85rem' }}>✓</span>
                        : <span style={{ color: C.border, fontSize: '0.85rem' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{
                        background: p.active ? C.greenLight : C.cream,
                        color: p.active ? C.green : C.mid,
                        padding: '0.18rem 0.55rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700,
                      }}>
                        {p.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        <button onClick={() => handleEdit(p)}
                          style={{ padding: '0.28rem 0.65rem', border: `1.5px solid ${C.border}`, borderRadius: '6px', background: C.white, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, color: C.charcoal, transition: 'all 0.15s' }}>
                          Edit
                        </button>
                        {tracked && (
                          <button onClick={() => setExpandedPar(isParOpen ? null : p.id)}
                            style={{ padding: '0.28rem 0.65rem', border: `1.5px solid ${isParOpen ? C.yellowDark : C.border}`, borderRadius: '6px', background: isParOpen ? C.yellow : C.white, color: isParOpen ? C.charcoal : C.mid, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.15s' }}>
                            Par
                          </button>
                        )}
                        <button onClick={() => handleToggleActive(p.id)}
                          style={{ padding: '0.28rem 0.65rem', border: `1.5px solid ${p.active ? C.red + '60' : C.green + '60'}`, borderRadius: '6px', background: C.white, cursor: 'pointer', fontSize: '0.78rem', color: p.active ? C.red : C.green, fontWeight: 600, transition: 'all 0.15s' }}>
                          {p.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isParOpen && (
                    <tr key={`${p.id}-par`} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td colSpan={11} style={{ padding: 0 }}>
                        <ParLevelEditor product={p} onClose={() => setExpandedPar(null)} />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}